import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Crown, Plus, Trash2, Users, Search, Calculator,
  Building2, Loader2, UserPlus, Save, Edit2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const BLANK_PLAN = {
  name: '',
  description: '',
  plan_type: 'custom',
  is_public: false,
  // Pricing
  monthly_price: 0,
  annual_price: 0,
  commission_percentage: 8,
  lead_base_price: 20,
  leads_ad_credits_percentage: 0,
  // Limits
  max_listings: -1,
  listing_types_included: 5,
  team_members_included: 10,
  max_locations: 3,
  media_storage_mb: -1,
  // Features (bool)
  variants_enabled: true,
  events_ticketing_enabled: true,
  reviews_ratings_enabled: true,
  international_markets_enabled: false,
  zoho_invoice_enabled: false,
  gst_billing_enabled: true,
  // Status features (included | add_on | disabled)
  whatsapp_transactional: 'included',
  master_catalogue_access: 'included',
  // Support
  support_level: 'priority',
};

const FEATURE_FLAGS = [
  { key: 'variants_enabled', label: 'Variants / Product Options (Add-Ons)' },
  { key: 'events_ticketing_enabled', label: 'Events Ticket with QR & Reader App' },
  { key: 'reviews_ratings_enabled', label: 'Reviews & Ratings' },
  { key: 'international_markets_enabled', label: 'International Markets' },
  { key: 'zoho_invoice_enabled', label: 'Zoho Invoice' },
  { key: 'gst_billing_enabled', label: 'GST Billing' },
];

const SUPPORT_OPTIONS = [
  { value: 'email', label: 'Email Only' },
  { value: 'email_chat', label: 'Email + Chat' },
  { value: 'call', label: 'Phone / Call' },
  { value: 'priority', label: 'Priority (Dedicated)' },
];

const STATUS_OPTIONS = [
  { value: 'included', label: 'Included' },
  { value: 'add_on', label: 'Add-on' },
  { value: 'disabled', label: 'Disabled' },
];

// small helpers
const NInput = ({ label, field, plan, set, prefix, suffix, isInt }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-gray-700">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
      <Input
        type="number"
        value={plan[field] ?? ''}
        onChange={e => set(field, isInt ? (parseInt(e.target.value) || 0) : (parseFloat(e.target.value) || 0))}
        className={prefix ? 'pl-7' : suffix ? 'pr-10' : ''}
        data-testid={`field-${field}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>}
    </div>
    {plan[field] === -1 && <p className="text-xs text-blue-600">-1 = Unlimited</p>}
  </div>
);

const StatusSelect = ({ label, field, plan, set }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-gray-700">{label}</Label>
    <Select value={plan[field]} onValueChange={v => set(field, v)}>
      <SelectTrigger data-testid={`field-${field}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const CustomPlansTab = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  const [customPlans, setCustomPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPlanId, setEditPlanId] = useState(null); // null = create mode, string = edit mode
  const [showAssign, setShowAssign] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plan, setPlanState] = useState(BLANK_PLAN);

  const openCreate = () => { setPlanState(BLANK_PLAN); setEditPlanId(null); setShowCreate(true); };
  const openEdit = (p) => {
    // Merge plan data over BLANK_PLAN defaults to ensure all keys exist
    setPlanState({ ...BLANK_PLAN, ...p });
    setEditPlanId(p.id);
    setShowCreate(true);
  };

  // vendor search
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [vLoading, setVLoading] = useState(false);

  // calculator
  const [calcIn, setCalcIn] = useState({ expected_listings: 100, expected_monthly_leads: 50, expected_storage_gb: 5 });
  const [calcOut, setCalcOut] = useState(null);

  const set = (k, v) => setPlanState(prev => ({ ...prev, [k]: v }));

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/custom-plans/');
      setCustomPlans(data || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load plans', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const searchVendors = useCallback(async (q) => {
    setVLoading(true);
    try {
      const data = await apiCall(`/api/custom-plans/vendors/search?q=${encodeURIComponent(q)}&limit=50`);
      setVendors(data.vendors || []);
    } catch { /* ignore */ }
    finally { setVLoading(false); }
  }, [apiCall]);

  useEffect(() => { if (showAssign) searchVendors(vendorSearch); }, [vendorSearch, showAssign, searchVendors]);

  const handleSave = async () => {
    if (!plan.name.trim()) {
      toast({ title: 'Error', description: 'Plan name is required', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      if (editPlanId) {
        // Edit mode — PUT
        await apiCall(`/api/custom-plans/${editPlanId}`, { method: 'PUT', body: JSON.stringify(plan) });
        toast({ title: 'Plan updated', description: `"${plan.name}" has been saved` });
      } else {
        // Create mode — POST
        const res = await apiCall('/api/custom-plans/', { method: 'POST', body: JSON.stringify(plan) });
        if (!res.success) throw new Error('Create failed');
        toast({ title: 'Plan created', description: `"${plan.name}" is ready to assign` });
      }
      setShowCreate(false);
      setPlanState(BLANK_PLAN);
      setEditPlanId(null);
      fetchPlans();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!activePlan || !selected.length) return;
    try {
      setSaving(true);
      await apiCall(`/api/custom-plans/${activePlan.id}/assign-vendors`, {
        method: 'POST', body: JSON.stringify({ vendor_ids: selected })
      });
      toast({ title: 'Vendors assigned', description: `${selected.length} vendor(s) assigned to "${activePlan.name}"` });
      setShowAssign(false);
      setSelected([]);
      fetchPlans();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleCalc = async () => {
    if (!activePlan) return;
    try {
      const res = await apiCall(`/api/custom-plans/${activePlan.id}/calculate-pricing`, {
        method: 'POST', body: JSON.stringify(calcIn)
      });
      setCalcOut(res);
    } catch { toast({ title: 'Error', description: 'Calculation failed', variant: 'destructive' }); }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Deactivate this plan?')) return;
    try {
      await apiCall(`/api/custom-plans/${planId}`, { method: 'DELETE' });
      toast({ title: 'Plan deactivated' });
      fetchPlans();
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Custom Enterprise Plans</h2>
          <p className="text-sm text-gray-500">Private plans with custom pricing for large vendors</p>
        </div>
        <Button onClick={openCreate} data-testid="create-custom-plan-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Custom Plan
        </Button>
      </div>

      {/* Plans */}
      {customPlans.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Custom Plans Yet</h3>
            <p className="text-sm text-gray-500 mb-4 text-center max-w-sm">
              Create custom plans for large vendors who need special limits, pricing, and feature access.
            </p>
            <Button variant="outline" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create First Plan
            </Button>          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {customPlans.map(p => (
            <Card key={p.id} data-testid={`plan-card-${p.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500 shrink-0" />
                    {p.name}
                  </CardTitle>
                  <Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status}</Badge>
                </div>
                {p.description && <CardDescription className="text-xs mt-0.5">{p.description}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Monthly</div>
                    <div className="font-semibold text-sm">{p.monthly_price > 0 ? `₹${p.monthly_price.toLocaleString('en-IN')}` : 'Custom'}</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Commission</div>
                    <div className="font-semibold text-sm">{p.commission_percentage}%</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Listings</div>
                    <div className="font-semibold text-sm">{p.max_listings === -1 ? '∞' : p.max_listings}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 border-t pt-2">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {p.assigned_vendor_count || 0} assigned</span>
                  <span className="capitalize">{(p.support_level || 'priority').replace('_', ' ')} support</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}>
                    <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setActivePlan(p); setShowAssign(true); }} data-testid={`assign-${p.id}`}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setActivePlan(p); setCalcOut(null); setShowCalc(true); }} data-testid={`calc-${p.id}`}>
                    <Calculator className="h-3.5 w-3.5 mr-1" /> Calculate
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── CREATE PLAN DIALOG ── */}
      <Dialog open={showCreate} onOpenChange={open => { if (!open) { setShowCreate(false); setEditPlanId(null); setPlanState(BLANK_PLAN); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Crown className="h-5 w-5 text-amber-500" />
              {editPlanId ? 'Edit Custom Plan' : 'Create Custom Plan'}
            </DialogTitle>
            <DialogDescription>
              {editPlanId ? 'Update pricing, limits, and feature access for this plan' : 'Configure pricing, limits, and feature access for this plan'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-6 py-5 space-y-6">

              {/* Plan Identity */}
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">Plan Name <span className="text-red-500">*</span></Label>
                  <Input placeholder="e.g., Enterprise Gold" value={plan.name} onChange={e => set('name', e.target.value)} data-testid="plan-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700">Plan Type</Label>
                    <Select value={plan.plan_type} onValueChange={v => set('plan_type', v)}>
                      <SelectTrigger data-testid="plan-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                        <SelectItem value="institutional">Institutional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-gray-700">Support Type</Label>
                    <Select value={plan.support_level} onValueChange={v => set('support_level', v)}>
                      <SelectTrigger data-testid="field-support_level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SUPPORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">Description</Label>
                  <Textarea placeholder="Brief description for this plan..." value={plan.description} onChange={e => set('description', e.target.value)} rows={2} />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={plan.is_public} onCheckedChange={v => set('is_public', v)} data-testid="is-public-switch" />
                  <Label className="text-sm text-gray-600">Make Public (visible to all vendors)</Label>
                </div>
              </div>

              {/* Pricing & Revenue */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b">Pricing & Revenue</h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <NInput label="Monthly Price (₹)" field="monthly_price" plan={plan} set={set} prefix="₹" />
                  <NInput label="Annual Price (₹)" field="annual_price" plan={plan} set={set} prefix="₹" />
                  <NInput label="Commission (%)" field="commission_percentage" plan={plan} set={set} suffix="%" />
                  <NInput label="Lead Base Price (₹)" field="lead_base_price" plan={plan} set={set} prefix="₹" />
                  <NInput label="Ad Credits (%)" field="leads_ad_credits_percentage" plan={plan} set={set} suffix="%" />
                </div>
              </div>

              {/* Limits & Capacity */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b">Limits & Capacity</h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <NInput label="No. of Listings" field="max_listings" plan={plan} set={set} isInt />
                  <NInput label="No. of Listing Types" field="listing_types_included" plan={plan} set={set} isInt />
                  <NInput label="No. of Team Members" field="team_members_included" plan={plan} set={set} isInt />
                  <NInput label="No. of Locations" field="max_locations" plan={plan} set={set} isInt />
                  <NInput label="Media Storage (MB)" field="media_storage_mb" plan={plan} set={set} isInt />
                </div>
              </div>

              {/* Status-based Features */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b">Feature Access</h3>
                <div className="grid grid-cols-2 gap-4">
                  <StatusSelect label="WhatsApp Transactional Msgs" field="whatsapp_transactional" plan={plan} set={set} />
                  <StatusSelect label="Master Catalogues Access" field="master_catalogue_access" plan={plan} set={set} />
                </div>
              </div>

              {/* Boolean Feature Toggles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b">Feature Toggles</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {FEATURE_FLAGS.map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                      <Label className="text-sm text-gray-700 cursor-pointer">{label}</Label>
                      <Switch
                        checked={!!plan[key]}
                        onCheckedChange={v => set(key, v)}
                        data-testid={`toggle-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditPlanId(null); setPlanState(BLANK_PLAN); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editPlanId ? 'Save Changes' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ASSIGN VENDORS DIALOG ── */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Assign Vendors — {activePlan?.name}</DialogTitle>
            <DialogDescription>Search and select vendors to assign to this plan</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0 flex flex-col">
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by name, email, store..." value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} className="pl-10" data-testid="vendor-search-input" />
            </div>
            {selected.length > 0 && (
              <div className="flex items-center justify-between bg-blue-50 px-3 py-1.5 rounded shrink-0">
                <span className="text-sm text-blue-700 font-medium">{selected.length} vendor(s) selected</span>
                <Button variant="ghost" size="sm" onClick={() => setSelected([])}>Clear</Button>
              </div>
            )}
            <ScrollArea className="flex-1 border rounded-lg min-h-0">
              {vLoading ? (
                <div className="flex items-center justify-center h-40"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : vendors.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No vendors found</div>
              ) : (
                <div className="divide-y">
                  {vendors.map(v => (
                    <div key={v.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(prev => prev.includes(v.id) ? prev.filter(id => id !== v.id) : [...prev, v.id])} data-testid={`vendor-row-${v.id}`}>
                      <Checkbox checked={selected.includes(v.id)} onCheckedChange={() => {}} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{v.store_name || v.name}</div>
                        <div className="text-xs text-gray-500 truncate">{v.email}</div>
                      </div>
                      {v.custom_plan && <Badge variant="outline" className="text-xs shrink-0">{v.custom_plan.name}</Badge>}
                      <Badge variant={v.subscription_status === 'active' ? 'default' : 'secondary'} className="text-xs shrink-0">
                        {v.subscription_status || 'none'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter className="pt-3 shrink-0">
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={saving || selected.length === 0} data-testid="confirm-assign-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Assign {selected.length > 0 ? `${selected.length} Vendor(s)` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── PRICING CALCULATOR DIALOG ── */}
      <Dialog open={showCalc} onOpenChange={setShowCalc}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Pricing Calculator — {activePlan?.name}
            </DialogTitle>
            <DialogDescription>Estimate vendor costs based on expected usage</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label>Expected Listings</Label>
                <Input type="number" value={calcIn.expected_listings} onChange={e => setCalcIn({ ...calcIn, expected_listings: parseInt(e.target.value) || 0 })} data-testid="calc-listings" />
              </div>
              <div className="space-y-1.5">
                <Label>Expected Monthly Leads</Label>
                <Input type="number" value={calcIn.expected_monthly_leads} onChange={e => setCalcIn({ ...calcIn, expected_monthly_leads: parseInt(e.target.value) || 0 })} data-testid="calc-leads" />
              </div>
              <div className="space-y-1.5">
                <Label>Expected Storage (GB)</Label>
                <Input type="number" value={calcIn.expected_storage_gb} onChange={e => setCalcIn({ ...calcIn, expected_storage_gb: parseFloat(e.target.value) || 0 })} data-testid="calc-storage" />
              </div>
            </div>
            <Button className="w-full" onClick={handleCalc} data-testid="run-calc-btn">Calculate</Button>
            {calcOut && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                {[
                  { label: 'Listings Cost', cost: calcOut.listings_cost, breakdown: calcOut.listings_breakdown },
                  { label: 'Leads Cost', cost: calcOut.leads_cost, breakdown: calcOut.leads_breakdown },
                  { label: 'Storage Cost', cost: calcOut.storage_cost, breakdown: calcOut.storage_breakdown },
                ].map(({ label, cost, breakdown }) => (
                  <div key={label}>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">{label}</span><span className="font-medium">₹{cost.toLocaleString()}</span></div>
                    <div className="text-xs text-gray-400">{breakdown}</div>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold"><span>Monthly Total</span><span className="text-blue-600">₹{calcOut.total_monthly.toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm text-gray-500 mt-1"><span>Annual ({calcOut.annual_discount}% off)</span><span>₹{calcOut.total_annual.toLocaleString()}</span></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalc(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CustomPlansTab;
